import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AdminAddonRole, AdminBaseRole, AdminSection, ApprovalKind } from '../api/types';

export interface Admin {
  id: string;
  email: string;
  name?: string | null;
  role: 'super_admin' | 'user';
  user_type?: AdminBaseRole;
  addons?: AdminAddonRole[];
  approvals?: ApprovalKind[];
  telegram_linked?: boolean;
  assistant_of_admin_id?: string | null;
  permissions: AdminSection[];
  app_access_mode?: 'legacy_all' | 'assigned';
  read_only: boolean;
  is_active: boolean;
  allowed_apps?: string[];
  allowed_tracker_campaigns?: string[];
  manual_tracker_campaigns?: string[];
  allowed_tracker_pwas?: string[];
}

interface AuthState {
  token: string | null;
  admin: Admin | null;
  setAuth: (token: string, admin: Admin) => void;
  setAdmin: (admin: Admin) => void;
  clearAuth: () => void;
  isAuthenticated: () => boolean;
  hasSection: (section: AdminSection) => boolean;
  canEdit: () => boolean;
  isQa: () => boolean;
  appInScope: (id: string) => boolean;
  trackerInScope: (id: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      admin: null,

      setAuth: (token, admin) => set({ token, admin }),
      setAdmin: (admin) => set({ admin }),
      clearAuth: () => set({ token: null, admin: null }),

      isAuthenticated: () => !!get().token,

      hasSection: (section) => {
        const a = get().admin;
        if (!a) return false;
        // Tolerate stale persisted profiles missing the new fields — Layout
        // refreshes /auth/me on mount, which fills them in.
        if (!a.role || a.role === 'super_admin') return true;
        if (section === 'users') return false;
        const perms = Array.isArray(a.permissions) ? a.permissions : [];
        return perms.includes(section);
      },

      canEdit: () => {
        const a = get().admin;
        if (!a) return false;
        if (!a.role || a.role === 'super_admin') return true;
        return !a.read_only;
      },

      // QA — either the `qa` user type (base `general` + the addon) or the addon
      // stacked on another base role. Mirrors qaTrackerRestricted() on the API:
      // no tracker Overview, conversions only for the current day.
      isQa: () => {
        const a = get().admin;
        if (!a) return false;
        if (!a.role || a.role === 'super_admin') return false;
        return (a.addons ?? []).includes('qa');
      },

      appInScope: (id) => {
        const a = get().admin;
        if (!a || !a.role || a.role === 'super_admin') return true;
        if (a.app_access_mode !== 'assigned') return true;
        const list = a.allowed_apps ?? [];
        return list.includes(id);
      },

      trackerInScope: (id) => {
        const a = get().admin;
        if (!a || !a.role || a.role === 'super_admin') return true;
        if (a.user_type !== 'traffic' && a.app_access_mode !== 'assigned') return true;
        const list = a.allowed_tracker_campaigns ?? [];
        return list.includes(id);
      },
    }),
    {
      name: 'zeywin-auth',
      partialize: (state) => ({
        token: state.token,
        admin: state.admin,
      }),
    }
  )
);
